import { useInfiniteQuery } from "@tanstack/react-query"
import { Link, useSearchParams } from "react-router-dom"
import GameScreenLayout from "@/components/GameScreenLayout"
import { getChampionFundHistory } from "@/api/championFundApi"
import { useAuth } from "@/context/AuthContext"
import { resolveProfileCityId } from "@/features/passport/guardiansCity"

function formatCoins(n: number): string {
  return n.toLocaleString("ru-RU")
}

export default function PastChampionsPage() {
  const [params] = useSearchParams()
  const { profile } = useAuth()
  const cityIdParam = params.get("cityId")
  const cityId =
    cityIdParam != null && cityIdParam !== ""
      ? Number(cityIdParam)
      : resolveProfileCityId(profile) ?? undefined

  const q = useInfiniteQuery({
    queryKey: ["champion-fund-history", cityId],
    queryFn: ({ pageParam }) =>
      getChampionFundHistory({
        cityId,
        cursor: pageParam as string | undefined,
        limit: 20,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: cityId != null,
  })

  const firstPage = q.data?.pages[0]
  const items = q.data?.pages.flatMap((p) => p.items) ?? []

  return (
    <GameScreenLayout title="Прошлые победители" backTo="/play">
      {cityId == null ? (
        <p className="text-sm text-guardians-muted">Не выбран город</p>
      ) : q.isLoading ? (
        <p className="text-sm text-guardians-muted">Загрузка…</p>
      ) : (
        <>
          {firstPage?.cityName ? (
            <p className="mb-3 text-xs text-guardians-muted">
              {firstPage.cityName}
            </p>
          ) : null}

          {items.length === 0 ? (
            <p className="text-sm text-guardians-muted">
              Пока не было выплат фонда чемпионов
            </p>
          ) : (
            <ul className="space-y-2">
              {items.map((item) => {
                const w = item.winner
                const canOpen = Boolean(w?.publicGameCode?.trim())
                const inner = (
                  <>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-bold text-guardians-accent">
                        {item.seasonTitle}
                      </p>
                      <p className="truncate text-base font-extrabold text-white">
                        {w?.nickname ?? "—"}
                      </p>
                      {w ? (
                        <p className="text-xs text-white/50">
                          {formatCoins(w.localPts)} очк. · приз{" "}
                          {formatCoins(w.prizeCoins)} мон.
                        </p>
                      ) : null}
                    </div>
                    {canOpen ? (
                      <span className="text-guardians-accent">→</span>
                    ) : null}
                  </>
                )
                return (
                  <li key={item.eventId}>
                    {canOpen ? (
                      <Link
                        to={`/play/player/${encodeURIComponent(w!.publicGameCode)}`}
                        className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.04] px-3 py-3 active:bg-white/10"
                      >
                        {inner}
                      </Link>
                    ) : (
                      <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.04] px-3 py-3">
                        {inner}
                      </div>
                    )}
                  </li>
                )
              })}
            </ul>
          )}

          {q.hasNextPage ? (
            <button
              type="button"
              className="mt-4 w-full rounded-xl border border-guardians-accent/40 py-2.5 text-sm font-bold text-guardians-accent"
              onClick={() => q.fetchNextPage()}
              disabled={q.isFetchingNextPage}
            >
              {q.isFetchingNextPage ? "Загрузка…" : "Ещё"}
            </button>
          ) : null}
        </>
      )}
    </GameScreenLayout>
  )
}
